/**
 * Query builders + keys for MEDIA (F3-F), local-first over PowerSync (Drizzle).
 * `video_asset` syncs in the course streams (without storage_key);
 * `video_watch_progress` in `my_learning`. Offline reading; playback is
 * signed separately via REST.
 */

import {
  and,
  asc,
  eq,
  isNull,
  type PowerSyncDrizzleDB,
  video_asset,
  video_watch_progress,
} from "@space/powersync";
import { watchProgressId } from "./watch-progress";

export const mediaQueryKeys = {
  all: ["media"] as const,
  asset: (videoId: string | undefined) => ["media", "asset", videoId] as const,
  byCourse: (courseId: string | undefined) => ["media", "course", courseId] as const,
  watch: (videoId: string | undefined, userId: string | undefined) =>
    ["media", "watch", userId, videoId] as const,
  watchAll: (userId: string | undefined) => ["media", "watch-all", userId] as const,
};

/** A `video_asset` by id (client view, without server-only columns). */
export function getVideoAssetQuery(db: PowerSyncDrizzleDB, videoId: string) {
  return db
    .select()
    .from(video_asset)
    .where(and(eq(video_asset.id, videoId), isNull(video_asset.deleted_at)))
    .limit(1);
}

/** Active videos of a course (author library / picker). */
export function getCourseVideosQuery(db: PowerSyncDrizzleDB, courseId: string) {
  return db
    .select()
    .from(video_asset)
    .where(and(eq(video_asset.course_id, courseId), isNull(video_asset.deleted_at)))
    .orderBy(asc(video_asset.created_at));
}

/** User's watch progress for a video (deterministic id). */
export function getWatchProgressQuery(db: PowerSyncDrizzleDB, userId: string, videoId: string) {
  return db
    .select()
    .from(video_watch_progress)
    .where(eq(video_watch_progress.id, watchProgressId(userId, videoId)))
    .limit(1);
}

/** All of the user's watch progress (for "continue watching…" badges). */
export function getAllWatchProgressQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db.select().from(video_watch_progress).where(eq(video_watch_progress.user_id, userId));
}
